import type { ReactNode } from "react"

import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"

const SURFACE_SECTION_CLASS =
  "shell-panel rounded-4xl border border-border/70 p-5 sm:p-6"

interface FoundationLayoutSkeletonProps {
  children?: ReactNode
  navItems?: number
  srTitle?: string
  testId?: string
}

export function FoundationLayoutSkeleton({
  children,
  navItems = 5,
  srTitle,
  testId,
}: FoundationLayoutSkeletonProps) {
  return (
    <div className="min-h-svh px-4 py-5 sm:px-6 lg:px-8" data-testid={testId}>
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <section className="rounded-4xl border border-border/70 px-5 py-5 sm:px-7 sm:py-7">
          {srTitle ? <span className="sr-only">{srTitle}</span> : null}
          <div className="flex flex-col gap-5">
            <div className="flex max-w-3xl flex-col gap-3">
              <Skeleton className="h-10 w-full max-w-xl sm:h-12" />
              <Skeleton className="h-5 w-full max-w-2xl" />
              <Skeleton className="h-5 w-4/5 max-w-xl" />
            </div>
          </div>
          <Separator className="my-6" />
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {Array.from({ length: navItems }).map((_, index) => (
              <div
                key={`foundation-skeleton-nav-${index}`}
                className="rounded-2xl border border-border/70 px-4 py-3"
              >
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              </div>
            ))}
          </div>
        </section>

        {children ?? null}
      </div>
    </div>
  )
}

interface FoundationCardSkeletonProps {
  rows?: number
  testId?: string
}

export function FoundationCardSkeleton({
  rows = 3,
  testId,
}: FoundationCardSkeletonProps) {
  return (
    <Card className="rounded-4xl border-border/70" data-testid={testId}>
      <CardHeader className="gap-3">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-full max-w-lg" />
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {Array.from({ length: rows }).map((_, index) => (
          <Skeleton
            key={`foundation-card-row-${index}`}
            className="h-12 w-full rounded-2xl"
          />
        ))}
      </CardContent>
    </Card>
  )
}

interface PublicLookupSectionSkeletonProps {
  showStats?: boolean
  testId?: string
}

export function PublicLookupSectionSkeleton({
  showStats = false,
  testId,
}: PublicLookupSectionSkeletonProps) {
  return (
    <section className={SURFACE_SECTION_CLASS} data-testid={testId}>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-9 w-full max-w-lg" />
        <Skeleton className="h-4 w-full max-w-3xl" />
        <Skeleton className="h-4 w-4/5 max-w-2xl" />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <div className="self-end">
          <Skeleton className="h-10 w-28 rounded-xl" />
        </div>
      </div>

      <Skeleton className="mt-4 h-16 w-full rounded-3xl" />

      {showStats ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`public-lookup-stat-${index}`}
              className="rounded-3xl border border-border/70 bg-background/70 p-4"
            >
              <div className="flex flex-col gap-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-5 w-28" />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  )
}

interface PublicSidebarSectionSkeletonProps {
  rows?: number
  testId?: string
}

export function PublicSidebarSectionSkeleton({
  rows = 3,
  testId,
}: PublicSidebarSectionSkeletonProps) {
  return (
    <section className={SURFACE_SECTION_CLASS} data-testid={testId}>
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-40" />
        {Array.from({ length: rows }).map((_, index) => (
          <Skeleton
            key={`public-sidebar-row-${index}`}
            className="h-5 w-full rounded-xl"
          />
        ))}
      </div>
    </section>
  )
}

interface AccountSessionSkeletonProps {
  testId?: string
}

export function AccountSessionSkeleton({
  testId,
}: AccountSessionSkeletonProps) {
  return (
    <Card
      className="rounded-4xl border-border/70 bg-background/70"
      data-testid={testId}
    >
      <CardHeader className="gap-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-full max-w-xs" />
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Skeleton className="h-12 w-full rounded-3xl" />
        <Skeleton className="h-12 w-full rounded-3xl" />
      </CardContent>
    </Card>
  )
}

interface OwnerPortalLoadingSkeletonProps {
  testId?: string
}

export function OwnerPortalLoadingSkeleton({
  testId,
}: OwnerPortalLoadingSkeletonProps) {
  return (
    <Card className={SURFACE_SECTION_CLASS} data-testid={testId}>
      <CardContent className="grid gap-5 px-0 py-0">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-36" />
          <Skeleton className="h-9 w-full max-w-md" />
          <Skeleton className="h-4 w-full max-w-2xl" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={`owner-summary-${index}`}
              className="h-24 w-full rounded-3xl"
            />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48 w-full rounded-4xl" />
          <Skeleton className="h-48 w-full rounded-4xl" />
        </div>
      </CardContent>
    </Card>
  )
}

interface BackendHealthSkeletonProps {
  testId?: string
}

export function BackendHealthSkeleton({
  testId,
}: BackendHealthSkeletonProps) {
  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-4 w-full max-w-xs" />
      <div className="grid gap-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={`backend-health-row-${index}`}
            className="h-8 w-full rounded-xl"
          />
        ))}
      </div>
    </div>
  )
}
